import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit } from '@angular/core';

import { ResizeListenerService } from '~/core/services/resize-listener.service';
import { PromotionBonusFacadeService } from '~/core/services/promotion-bonus-facade.service';
import { NavigationService } from '~/core/services/navigation.service';
import { HelperPlus2Service } from '~/core/services/helper-plus2.service';
import { SportsbookIntegrationService } from '~/pages/sportsbook-integration/sportsbook-integration.service';
import { PromotionBonusTopbarPlus2Component } from '~/components/promotion-bonus-topbar-plus2/promotion-bonus-topbar-plus2.component';
import { MenuTopBarPlus2Component } from '~/containers/menu-top-bar-plus2/menu-top-bar-plus2.component';
import { MenuTopBarMobileComponent } from '~/containers/menu-top-bar-mobile/menu-top-bar-mobile.component';
import { HeaderConfigService } from '~/core/services/header-config.service';
import { isDesktop } from '~/core/helpers/device-utils';

@Component({
  selector: 'app-window-header',
  templateUrl: './window-header.component.html',
  styleUrls: ['./window-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PromotionBonusTopbarPlus2Component, MenuTopBarPlus2Component, MenuTopBarMobileComponent],
})
export class WindowHeaderComponent implements OnInit {
  protected readonly resizeListener = inject(ResizeListenerService);
  protected readonly promoFacade = inject(PromotionBonusFacadeService);
  protected readonly navigation = inject(NavigationService);
  protected readonly helper = inject(HelperPlus2Service);
  protected readonly headerConfigService = inject(HeaderConfigService);
  private readonly sportsbookIntegration = inject(SportsbookIntegrationService);

  readonly isDesktopView = computed(() => !this.resizeListener.isMobile());

  readonly hideMenuTop = computed(() => this.sportsbookIntegration.isIframe);

  readonly showPromotionTopbar = computed(() => {
    return this.promoFacade.shouldShowTopbar() && !this.navigation.hasCasinoGameOpenedSignal();
  });

  readonly showDesktopHeader = computed(() => {
    const headerEnabled = this.headerConfigService.hasDesktopHeader() || this.headerConfigService.hasDesktopSubheader();
    return !(this.helper.casinoGameOnFullScreenMode() || this.hideMenuTop()) && headerEnabled;
  });

  readonly showMobileHeader = computed(() => {
    if (this.hideMenuTop()) {
      return false;
    }
    const headerEnabled = this.headerConfigService.hasMobileHeader() || this.headerConfigService.hasMobileSubheader();
    const url = this.helper.currentURLScreenSignal();
    const isRules = url.includes('rules');
    const isCasinoGame = this.navigation.hasCasinoGameOpenedSignal();
    const isLoginPage = url.includes('signin');
    const isRegisterPage = url.includes('register');
    return !(isRules || isCasinoGame || isLoginPage || isRegisterPage) && headerEnabled;
  });

  readonly mobileHeaderVisible = this.helper.mobileHeaderVisible;

  readonly isCasinoOrLive = computed(() => {
    const url = this.helper.currentURLScreenSignal();
    return url.includes('casino');
  });

  readonly isVirtualSports = computed(() => {
    const url = this.helper.currentURLScreenSignal();
    return url.includes('virtual-sports');
  });

  readonly hideMobileSecondBar = computed(() => {
    const url = this.helper.currentURLScreenSignal();
    const hasGameParams = url.includes('gameId=') && url.includes('extra=');
    return hasGameParams || this.isVirtualSports() || this.hideMenuTop();
  });

  constructor() {
    effect(() => {
      this.showPromotionTopbar();
      this.calculateHeaderHeight();
    });
  }

  ngOnInit() {
    this.calculateHeaderHeight();
  }

  calculateHeaderHeight() {
    if (this.showMobileHeader() && !isDesktop()) {
      let headerHeight = 64;
      if (this.headerConfigService.hasMobileSubheader()) {
        headerHeight += 32;
      }

      if (this.showPromotionTopbar()) {
        headerHeight += 50;
      }

      if (document.getElementById('window__container')) {
        document.getElementById('window__container').style.setProperty('--header-height', `${headerHeight}px`);
      }
    }
  }

  onToggleSportsMenu(): void {
    this.helper.toggleSportsMenuDrawer.next(undefined);
  }

  onToggleLoginSideMenu(): void {
    this.helper.toggleLoginSideMenuEvent.emit();
  }

  onOpenGuestDeposit(): void {
    this.helper.guestDepositEvent.next();
  }

  onPromoTopbarClose(): void {
    this.promoFacade.closeTopbar();
  }

  onPromoTopbarClaim(): void {
    this.promoFacade.claimTopbar();
  }
}
